import React, {
  FC,
  ReactText,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Form, Spin, Tabs } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import axios from 'axios';
import { HttpService } from '@core/services';
import { ReportsHttpService } from '@core/services/http';
import { dateWorker } from '@core/utils/date-worker';
import { OverdueTaskRow, ReportKey, ReportsSortBy } from '@models/interfaces';
import { PrimaryButton } from '@shared/modules';
import { ReportsActions } from '@store/actions';
import { getReportsFilterHash } from '@store/reducers/reports.reducer';
import { ReportsSelectors, UserSelectors } from '@store/selectors';
import {
  DEFAULT_REPORT_FILTERS,
  getProjectTypeFilterValues,
  getProjectTypeOptions,
  getReportDateRangeFields,
  getReportFiltersForReportChange,
  getReportRowRedirectUrl,
  getReportsSortOrder,
  getTableSorter,
  isInteractiveRowClick,
  openReportRowInNewTab,
  REPORT_TABS,
  ReportsFilterFormValues,
  sanitizeReportsFiltersForReport,
} from '../../models';
import { isPermittingReport } from '../../models/helpers';
import { getRoleReportKeys } from '../../models/reports-access';
import { DatesAtAGlanceReport } from '../dates-at-a-glance-report';
import { NewClosedProjectsReport } from '../new-closed-projects-report';
import { OverdueTasksReport } from '../overdue-tasks-report';
import { PermittingReport } from '../permitting-report';
import { ProjectDurationReport } from '../project-duration-report';
import { ReportsFilters } from '../reports-filters';
import { StructuralPassFailReport } from '../structural-pass-fail-report';

import './styles.scss';

const REPORT_ERROR_MESSAGES = {
  PERMITTING_REPORT_CONFIGURATION:
    'Permitting Overview is unavailable: required date sources are missing or ambiguous. Contact an administrator.',
  PERMITTING_REPORT_INTEGRITY:
    'Permitting Overview is unavailable: conflicting Actual Dates must be resolved.',
};

export const ReportsBrowse: FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [ready, setReady] = useState(false);
  const service = useMemo(
    () => HttpService.getHttpRequests(ReportsHttpService),
    [],
  );
  const state = useSelector(ReportsSelectors.getReportsState);
  const roles = useSelector(UserSelectors.getUserRoles);
  const {
    filters,
    selectedReport,
    filterOptions,
    filterOptionsLoading,
    filterOptionsError,
  } = state;
  const enabledTabs = useMemo(() => {
    const allowed = getRoleReportKeys(roles);

    return REPORT_TABS.filter(
      tab =>
        allowed.includes(tab.key) &&
        (!filterOptions?.availableReports ||
          filterOptions.availableReports.includes(tab.key)),
    );
  }, [roles, filterOptions]);
  const title = new URLSearchParams(location.search).get('tabName');
  const activeTab =
    enabledTabs.find(tab => tab.title === title) ?? enabledTabs[0];
  const currentHash = getReportsFilterHash(filters);
  const options = filterOptions?.reportOptions[selectedReport];
  const loaded = state.loadedReports[selectedReport] === currentHash;
  const showReport = loaded || !!state.data[selectedReport];
  const loading = !!state.loadingReports[selectedReport];
  const error = state.errors[selectedReport];
  const errorMessage =
    typeof error === 'string'
      ? REPORT_ERROR_MESSAGES[error]
      : 'Unable to load selected report';

  useEffect(() => {
    dispatch(ReportsActions.invalidateReportsData());
    setReady(true);

    return () => {
      dispatch(ReportsActions.invalidateReportsData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!activeTab) return;

    if (activeTab.title !== title) {
      history.replace(
        `${location.pathname}?tabName=${encodeURIComponent(activeTab.title)}`,
      );
    }

    if (selectedReport !== activeTab.key) {
      dispatch(
        ReportsActions.setFilters(
          getReportFiltersForReportChange(
            activeTab.key,
            filters,
            filterOptions?.reportOptions,
          ),
        ),
      );
      dispatch(ReportsActions.setSelectedReport(activeTab.key));
    }
  }, [
    activeTab,
    dispatch,
    filterOptions,
    filters,
    history,
    location.pathname,
    selectedReport,
    title,
  ]);

  useEffect(() => {
    const range = getReportDateRangeFields(selectedReport);
    const from = range ? filters[range[0]] : undefined;
    const to = range ? filters[range[1]] : undefined;

    form.resetFields();
    form.setFieldsValue({
      ...filters,
      dateRange: {
        from:
          typeof from === 'string'
            ? dateWorker(from, 'YYYY-MM-DD', true)
            : undefined,
        to:
          typeof to === 'string'
            ? dateWorker(to, 'YYYY-MM-DD', true)
            : undefined,
      },
    });
  }, [filters, form, selectedReport]);

  const loadOptions = useCallback(async (): Promise<void> => {
    const request = { requestId: crypto.randomUUID(), requestHash: 'options' };

    dispatch(ReportsActions.getFilterOptions.start(request));

    try {
      const data = await service.getFilterOptions();

      dispatch(ReportsActions.getFilterOptions.done({ ...request, data }));
    } catch {
      dispatch(ReportsActions.getFilterOptions.fail(request));
    }
  }, [dispatch, service]);

  useEffect(() => {
    if (
      ready &&
      !filterOptions &&
      !filterOptionsLoading &&
      !filterOptionsError
    ) {
      loadOptions();
    }
  }, [
    ready,
    filterOptions,
    filterOptionsLoading,
    filterOptionsError,
    loadOptions,
  ]);

  const loadReport = useCallback(async (): Promise<void> => {
    if (!enabledTabs.some(tab => tab.key === selectedReport)) return;

    const request = {
      requestId: crypto.randomUUID(),
      requestHash: getReportsFilterHash(filters),
    };
    const requestFilters = sanitizeReportsFiltersForReport(
      selectedReport,
      filters,
      filterOptions?.reportOptions,
    );
    const actions = {
      [ReportKey.PermittingOverview]: ReportsActions.getPermittingOverview,
      [ReportKey.BPExpiration]: ReportsActions.getBPExpiration,
      [ReportKey.OverdueTasks]: ReportsActions.getOverdueTasks,
      [ReportKey.StructuralPassFail]: ReportsActions.getStructuralPassFail,
      [ReportKey.DatesAtAGlance]: ReportsActions.getDatesAtAGlance,
      [ReportKey.ProjectDuration]: ReportsActions.getProjectDuration,
      [ReportKey.NewClosedProjects]: ReportsActions.getNewClosedProjects,
    };
    const action = actions[selectedReport];

    dispatch(action.start(request));

    try {
      const requests = {
        [ReportKey.PermittingOverview]: () =>
          service.getPermittingOverview(requestFilters),
        [ReportKey.BPExpiration]: () => service.getBPExpiration(requestFilters),
        [ReportKey.OverdueTasks]: () => service.getOverdueTasks(requestFilters),
        [ReportKey.StructuralPassFail]: () =>
          service.getStructuralPassFail(requestFilters),
        [ReportKey.DatesAtAGlance]: () =>
          service.getDatesAtAGlance(requestFilters),
        [ReportKey.ProjectDuration]: () =>
          service.getProjectDuration(requestFilters),
        [ReportKey.NewClosedProjects]: () =>
          service.getNewClosedProjects(requestFilters),
      };
      const data = await requests[selectedReport]();

      dispatch(action.done({ ...request, data }));
    } catch (failure) {
      const response: unknown = axios.isAxiosError(failure)
        ? failure.response?.data
        : undefined;
      const code: unknown =
        typeof response === 'object' && response !== null
          ? Reflect.get(response, 'code')
          : undefined;
      const errorCode =
        code === 'PERMITTING_REPORT_CONFIGURATION' ||
        code === 'PERMITTING_REPORT_INTEGRITY'
          ? code
          : undefined;

      dispatch(action.fail({ ...request, errorCode }));
    }
  }, [dispatch, filterOptions, filters, selectedReport, service, enabledTabs]);

  useEffect(() => {
    if (
      ready &&
      filterOptions &&
      activeTab?.key === selectedReport &&
      !loaded &&
      !loading &&
      !error
    ) {
      loadReport();
    }
  }, [
    ready,
    filterOptions,
    activeTab?.key,
    selectedReport,
    loaded,
    loading,
    error,
    loadReport,
  ]);

  const onApplyFilters = async (): Promise<void> => {
    let values: ReportsFilterFormValues;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const { dateRange, ...formFilters } = values;

    if (
      selectedReport === ReportKey.PermittingOverview &&
      (dateRange?.from || dateRange?.to) &&
      !formFilters.dateField
    ) {
      form.setFields([
        {
          name: 'dateField',
          errors: ['Choose a Date Field for the date range.'],
        },
      ]);

      return;
    }

    const range = getReportDateRangeFields(selectedReport);
    const dates = range
      ? {
          [range[0]]: dateRange?.from
            ? dateRange.from.format('YYYY-MM-DD')
            : undefined,
          [range[1]]: dateRange?.to
            ? dateRange.to.format('YYYY-MM-DD')
            : undefined,
        }
      : {};
    const nextFilters = sanitizeReportsFiltersForReport(
      selectedReport,
      {
        ...DEFAULT_REPORT_FILTERS,
        ...formFilters,
        ...dates,
        metric: filters.metric,
        page: 1,
        size: filters.size,
        projectType: getProjectTypeFilterValues(
          formFilters.projectType,
          filterOptions?.projectTypes ?? [],
        ),
      },
      filterOptions?.reportOptions,
    );

    dispatch(ReportsActions.setFilters(nextFilters));
  };

  const onTableChange = (
    pagination: PaginationConfig,
    _tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    { action }: TableCurrentDataSource<Store>,
  ): void => {
    const sorted = getTableSorter(sorter);
    const sortOrder =
      action === 'sort' ? getReportsSortOrder(sorted.order) : filters.sortOrder;
    let { sortBy } = filters;

    if (action === 'sort') {
      sortBy = sortOrder ? (sorted.columnKey as ReportsSortBy) : undefined;
    }

    dispatch(
      ReportsActions.setFilters(
        sanitizeReportsFiltersForReport(
          selectedReport,
          {
            ...filters,
            page:
              action === 'sort' || pagination.pageSize !== filters.size
                ? 1
                : pagination.current ?? 1,
            size: pagination.pageSize ?? filters.size,
            sortBy,
            sortOrder,
          },
          filterOptions?.reportOptions,
        ),
      ),
    );
  };

  const onRowClick = (
    row: OverdueTaskRow,
    _newTab: boolean,
    event: { target?: EventTarget | null },
  ): void => {
    const url = getReportRowRedirectUrl(row);

    if (url && !isInteractiveRowClick(event)) openReportRowInNewTab(url);
  };

  return (
    <div className="prov-reports-page">
      <header className="prov-reports-page__header">
        <h1>Reports</h1>
        {isPermittingReport(selectedReport) && (
          <Button
            disabled={!filterOptions || loading}
            onClick={() => {
              loadReport();
            }}
          >
            Refresh
          </Button>
        )}
      </header>
      {filterOptionsError && (
        <Button
          onClick={() => {
            loadOptions();
          }}
        >
          Retry filter options
        </Button>
      )}
      <ReportsFilters
        currentGroupByOptions={options?.groupByOptions ?? []}
        currentSortByOptions={options?.sortByOptions ?? []}
        filterOptions={filterOptions}
        filterOptionsError={filterOptionsError}
        filterOptionsLoading={filterOptionsLoading}
        form={form}
        onApplyFilters={onApplyFilters}
        onResetFilters={() => {
          dispatch(ReportsActions.setFilters({ ...DEFAULT_REPORT_FILTERS }));
        }}
        projectTypeOptions={getProjectTypeOptions(
          filterOptions?.projectTypes ?? [],
        )}
        selectedReport={selectedReport}
      />
      <section className="prov-reports-page__content">
        <Tabs
          activeKey={activeTab?.key}
          onChange={key => {
            const tab = enabledTabs.find(item => item.key === key);

            if (tab)
              history.push(
                `${location.pathname}?tabName=${encodeURIComponent(tab.title)}`,
              );
          }}
          items={enabledTabs.map(tab => ({ key: tab.key, label: tab.title }))}
        />
        {!activeTab && (
          <Alert
            type="error"
            message="No reports are available for your account."
          />
        )}
        {activeTab && !!error && (
          <Alert
            type="error"
            message={errorMessage}
            action={
              <PrimaryButton
                title="Retry"
                type="default"
                onClick={() => {
                  loadReport();
                }}
              />
            }
          />
        )}
        {activeTab && !error && (
          <Spin spinning={loading || filterOptionsLoading}>
            {showReport &&
              (selectedReport === ReportKey.PermittingOverview ||
                selectedReport === ReportKey.BPExpiration) && (
                <PermittingReport
                  reportKey={selectedReport}
                  data={state.data[selectedReport]}
                  loading={loading}
                  onTableChange={onTableChange}
                  sortByOptions={options?.sortByOptions ?? []}
                  dateFields={filterOptions?.permittingDateFields ?? []}
                />
              )}
            {showReport && selectedReport === ReportKey.NewClosedProjects && (
              <NewClosedProjectsReport
                data={state.data.newClosedProjects}
                filters={filters}
                onTableChange={onTableChange}
                sortByOptions={options?.sortByOptions ?? []}
              />
            )}
            {showReport && selectedReport === ReportKey.ProjectDuration && (
              <ProjectDurationReport
                data={state.data.projectDuration}
                filters={filters}
                onMetricChange={metric => {
                  dispatch(
                    ReportsActions.setFilters({ ...filters, metric, page: 1 }),
                  );
                }}
                onTableChange={onTableChange}
                groupByOptions={options?.groupByOptions ?? []}
                sortByOptions={options?.sortByOptions ?? []}
              />
            )}
            {showReport && selectedReport === ReportKey.DatesAtAGlance && (
              <DatesAtAGlanceReport
                data={state.data.datesAtAGlance}
                filters={filters}
                onTableChange={onTableChange}
                sortByOptions={options?.sortByOptions ?? []}
              />
            )}
            {showReport && selectedReport === ReportKey.StructuralPassFail && (
              <StructuralPassFailReport
                data={state.data.structuralPassFail}
                filters={filters}
                onTableChange={onTableChange}
                sortByOptions={options?.sortByOptions ?? []}
              />
            )}
            {showReport && selectedReport === ReportKey.OverdueTasks && (
              <OverdueTasksReport
                data={state.data.overdueTasks}
                filters={filters}
                onRowClick={onRowClick}
                onTableChange={onTableChange}
                sortByOptions={options?.sortByOptions ?? []}
              />
            )}
          </Spin>
        )}
      </section>
    </div>
  );
};
